import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './AboutHero.css';
import logoImg from '../../../assets/images/gamesplus.jpg';

const COLORS = ['#97c284', '#83cdbc', '#0e6447', '#52e2b7', '#57bc3e'];

const HexagonExplosion = ({ x, y }) => {
  const [hexagons] = useState(() => 
    Array.from({ length: 6 }, () => ({
      id: Math.random(),
      size: Math.random() * 30 + 15,
      angle: Math.random() * Math.PI * 2,
      speed: Math.random() * 150 + 100,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rotation: Math.random() * 360
    }))
  );

  return createPortal(
    <div className="hexagon-explosion" style={{ left: x, top: y }}>
      {hexagons.map(hex => (
        <div
          key={hex.id}
          className="explosion-hexagon"
          style={{
            '--size': `${hex.size}px`,
            '--angle': `${hex.angle}rad`,
            '--speed': `${hex.speed}px`,
            '--color': hex.color,
            '--rotation': `${hex.rotation}deg`
          }}
        />
      ))}
    </div>,
    document.body
  );
};

const AboutHero = () => {
  const [explosions, setExplosions] = useState([]);
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const logoRef = useRef(null);
  const achievementsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (titleRef.current) {
              titleRef.current.classList.add('visible');
            }
            if (contentRef.current) {
              setTimeout(() => {
                contentRef.current.classList.add('visible');
                const logo = contentRef.current.querySelector('.about-hero-logo');
                const text = contentRef.current.querySelector('.about-hero-text');
                if (logo) logo.classList.add('visible');
                if (text) {
                  setTimeout(() => {
                    text.classList.add('visible');
                  }, 400);
                }
              }, 200);
            }
            if (achievementsRef.current) {
              setTimeout(() => {
                achievementsRef.current.classList.add('animate-badges');
                
                // Trigger explosions to match badge animation timing exactly
                const badges = achievementsRef.current.querySelectorAll('.achievement-badge');
                badges.forEach((badge, index) => {
                  const explosionDelay = index === 0 ? 300 : 600; // Match the CSS animation delays
                  
                  setTimeout(() => {
                    const rect = badge.getBoundingClientRect();
                    const x = rect.left + rect.width / 2;
                    const y = rect.top + rect.height / 2;
                    
                    setExplosions(prev => [...prev, {
                      id: Date.now() + index,
                      x: x + window.scrollX,
                      y: y + window.scrollY
                    }]);
                  }, explosionDelay); // Use the same delays as the CSS animations
                });
              }, 1000);
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.50 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Clean up explosions after animation
  useEffect(() => {
    if (explosions.length > 0) {
      const timer = setTimeout(() => {
        setExplosions([]);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [explosions]);

  return (
    <div className="about-hero-section" ref={sectionRef}>
      <h2 className="about-hero-title" ref={titleRef}>Our Story</h2>
      <div className="about-hero-content" ref={contentRef}>
        <div className="about-hero-logo">
          <div className="logo-container">
            <img src={logoImg} alt="Paper Cactus Games Logo" />
            <div className="logo-shadow"></div>
          </div>
        </div>
        <div className="about-hero-text">
          <p>Paper Cactus Games crafts innovative 2.5D experiences that challenge the status quo, specializing in the roguelike genre with our unique approach to game design.</p>
          <p>From our creative hub in Adelaide, we're focused on blending cutting-edge technology with compelling artistic vision to create games that leave lasting impressions.</p>
          <div className="achievements" ref={achievementsRef}>
            <div className="achievement-badge">
              <i className="fas fa-award"></i>
              <span>2 Successful<br/>Kickstarters</span>
            </div>
            <div className="achievement-badge">
              <i className="fas fa-map-marker-alt"></i>
              <span>Made in<br/>Australia</span>
            </div>
          </div>
        </div>
      </div>
      <div className="hexagon-grid">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="decorative-hexagon"></div>
        ))}
      </div>
      {explosions.map(explosion => (
        <HexagonExplosion
          key={explosion.id}
          x={explosion.x}
          y={explosion.y}
        />
      ))}
    </div>
  );
};

export default AboutHero;
